import { useMemo } from 'react';
import useAuth from './useAuth';
import { useProfilePreviewOptional } from '../contexts/ProfilePreviewContext';
import {
  PermissionCode,
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
} from '../constants/permissions';

export function usePermissions() {
  const { user } = useAuth();
  const preview = useProfilePreviewOptional();

  const loginPermissions = (user?.permissions ?? []) as PermissionCode[];
  const effectivePermissions = preview?.effectivePermissions ?? loginPermissions;

  const isAdmin = useMemo(() => {
    const code = user?.access_profile?.code ?? user?.role;
    return code === 'admin';
  }, [user?.access_profile?.code, user?.role]);

  const can = useMemo(
    () => (code: PermissionCode) => hasPermission(effectivePermissions, code),
    [effectivePermissions]
  );

  const canAny = useMemo(
    () => (codes: PermissionCode[]) => hasAnyPermission(effectivePermissions, codes),
    [effectivePermissions]
  );

  const canAll = useMemo(
    () => (codes: PermissionCode[]) => hasAllPermissions(effectivePermissions, codes),
    [effectivePermissions]
  );

  return {
    permissions: effectivePermissions,
    loginPermissions,
    can,
    canAny,
    canAll,
    isAdmin,
    isPreviewActive: preview?.isPreviewActive ?? false,
    previewProfileCode: preview?.previewProfileCode ?? null,
    previewProfileName: preview?.previewProfileName ?? null,
    clearPreview: preview?.clearPreview,
    setPreviewProfile: preview?.setPreviewProfile,
  };
}

export default usePermissions;
